import React, { useState, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { DataTable } from 'primereact/datatable'
import { selectAllAssets } from 'providers/AssetLoader/store/selectors'
import { Toast } from 'primereact/toast'
import * as Columns from './Columns'
import { createColumn } from './LazyGridColumnFactory'
import { useLocalDataSync } from './hooks/useLocalDataSync'
import { useLazyLoading } from './hooks/useLazyLoading'
import { useRowEditingLazy } from './hooks/useRowEditingLazy'
import {
    PAGINATOR_POSITION,
    PAGINATOR_TEMPLATE,
    ROWS_PER_PAGE_OPTIONS_LAZY
} from './config/gridConstants'
import { assembleColumns } from './utils/assembleColumns'
import { createInitialFilterState } from './utils/createInitialFilterState'

// ReSharper disable once InconsistentNaming
const LazyGrid = (props) => {
    // Deconstruct props to get specific component related selectors and actions
    const {
        dataKey,
        totalCount,
        emptyMessage,
        columnDefinitions,
        selectTotalPages,
        selectFirst,
        selectRows,
        fetchStatusSelector,
        setTotalPagesAction,
        setCurrentPageAction,
        setFirstAction,
        setRowsAction,
        setFilterAction,
        setSortAction,
        selectionMode,
        selectionStyle,
        scrollHeight,
        allowRowEdits,
        header,
        isEditable,
        originalData,
        startEditing,
        stopEditing,
        rowExpansionTemplate,
        collapsedRowIcon,
        expandedRowIcon,
        // Selection props
        selection,
        onSelectionChange,
        onRowClick,
        rowClassName,
        onKeyDown,
        resetFiltersKey
    } = props

    const {
        channels
    } = useSelector(selectAllAssets)

    const dispatch = useDispatch()

    // Use specific domain related selectors and actions
    const first = useSelector(selectFirst)
    const rows = useSelector(selectRows)
    const fetchStatusSelect = useSelector(fetchStatusSelector)
    const fetchStatus = fetchStatusSelect['status']
    const selectPageCount = useSelector(selectTotalPages)

    // Row expansion state - use external state if provided, otherwise use internal
    const externalExpandedRows = props.expandedRows
    const externalOnRowToggle = props.onRowToggle
    const [internalExpandedRows, setInternalExpandedRows] = useState(rowExpansionTemplate ? null : undefined)

    // Use external state if provided, otherwise use internal
    const expandedRows = externalExpandedRows !== undefined ? externalExpandedRows : internalExpandedRows
    const handleRowToggle = externalOnRowToggle || ((e) => setInternalExpandedRows(e.data))

    // Toast ref for validation errors
    const toastRef = useRef(null)

    // Custom hooks
    const { localData, setLocalData } = useLocalDataSync({ data: props.data })

    // Create initial filter state from column definitions if not provided
    // This ensures PrimeReact has a valid filter structure for addRule() on initial load
    const initialFilterState = props.filterState ?? createInitialFilterState(columnDefinitions)

    const { multiSortMeta, filters, setFilters, setMultiSortMeta, lazyLoadData } = useLazyLoading({
        setCurrentPageAction,
        setFirstAction,
        setRowsAction,
        setSortAction,
        setFilterAction,
        initialFilterState: props.filterState ?? initialFilterState,
        initialSortState: props.sortState,
        setFilterStateAction: props.setFilterStateAction,
        setSortStateAction: props.setSortStateAction
    })

    // Keep local filter/sort UI state in sync with externally reset Redux state
    useEffect(() => {
        const filterStateToUse = props.filterState ?? initialFilterState
        setFilters(filterStateToUse)
    }, [props.filterState, setFilters])

    useEffect(() => {
        setMultiSortMeta(props.sortState ?? [])
    }, [props.sortState, setMultiSortMeta])

    // Reset filters when resetFiltersKey changes (e.g., new search)
    const prevResetKeyRef = useRef(resetFiltersKey)
    useEffect(() => {
        if (resetFiltersKey !== undefined && resetFiltersKey !== prevResetKeyRef.current) {
            setFilters(null)
            setMultiSortMeta([])
        }
        prevResetKeyRef.current = resetFiltersKey
    }, [resetFiltersKey, setFilters, setMultiSortMeta])

    const {
        validationErrors,
        isFieldChanged,
        onRowEditInit,
        onRowEditSave,
        rowEditValidator,
        onRowEditCancel
    } = useRowEditingLazy({
        dataKey,
        localData,
        setLocalData,
        originalData,
        data: props.data,
        validateData: props.validateData,
        onSave: props.onSave,
        startEditing,
        stopEditing,
        toastRef
    })

    // Calculate total pages and dispatch if different
    useEffect(() => {
        const pagesCount = totalCount > 0 ? Math.ceil(totalCount / rows) : 0
        if (selectPageCount !== pagesCount) {
            dispatch(setTotalPagesAction(pagesCount))
        }
    }, [totalCount, selectPageCount, dispatch, setTotalPagesAction])

    // Initial lazy load
    useEffect(() => {
        const lazyLoadEventData = { rows, first, multiSortMeta, filters }
        lazyLoadData(lazyLoadEventData)
    }, [rows, first, multiSortMeta, filters, lazyLoadData])

    // Create all columns from definitions
    const definedColumns = columnDefinitions.map((col) =>
        createColumn({
            columnType: col.columnType,
            title: col.title,
            mapsToField: col.mapsToField,
            filter: col.filter,
            expand: col.expand,
            template: col.template,
            sortable: col.sortable,
            filterMatchMode: col.filterMatchMode,
            filterElement: col.filterElement,
            editable: col.editable,
            isFieldChanged,
            isFieldChangedOverride: col.isFieldChangedOverride,
            validationErrors,
            dataKey,
            channels,
            isEditable,
            originalData,
            style: col.style
        })
    )

    // Assemble final column array with special columns
    const columns = assembleColumns({
        definedColumns,
        columnDefinitions,
        selectionMode,
        allowRowEdits,
        Columns,
        dataKey,
        expandedRows,
        onRowToggle: handleRowToggle
    })

    return (
        <div id={props.id + '_container'}>
            <Toast ref={toastRef} />
            <DataTable
                id={props.id + '_container_dataTable'}
                value={localData}
                dataKey={dataKey}
                size="small"
                emptyMessage={emptyMessage}
                scrollable
                scrollHeight={scrollHeight ?? 'calc(100vh - 500px)'}
                selectionMode={selectionStyle === "checkbox" ? "checkbox" : "single"}
                showGridlines
                stripedRows
                lazy
                paginator
                removableSort
                filters={filters}
                paginatorPosition={PAGINATOR_POSITION}
                paginatorTemplate={PAGINATOR_TEMPLATE}
                className="datatable-responsive"
                rows={rows}
                first={first}
                totalRecords={totalCount}
                header={header}
                {...(rowExpansionTemplate && {
                    expandedRows: expandedRows,
                    rowExpansionTemplate: rowExpansionTemplate,
                    collapsedRowIcon,
                    onRowToggle: handleRowToggle,
                    expandedRowIcon: row => expandedRowIcon(row, expandedRows)
                })}
                rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS_LAZY}
                onPage={e => lazyLoadData(e)}
                multiSortMeta={multiSortMeta}
                sortMode="multiple"
                editMode="row"
                onSort={e => lazyLoadData(e)}
                onFilter={e => lazyLoadData(e)}
                loading={fetchStatus === 'pending'}
                rowEditValidator={rowEditValidator}
                onRowEditCancel={onRowEditCancel}
                onRowEditInit={onRowEditInit}
                onRowEditSave={onRowEditSave}
                selection={selection}
                onSelectionChange={onSelectionChange}
                onRowClick={onRowClick}
                rowClassName={rowClassName}
                onKeyDown={onKeyDown}
            >
                {columns}
            </DataTable>
        </div>
    )

}

export default LazyGrid
